'use client';

import { c as _c } from "react/compiler-runtime";
import { LayoutContainer, Section } from '@petsmart-ui/sparky';
import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';
import type { FC } from 'react';
import { trackCardUpClick } from '@libs/analytics';
import { clsx } from '@libs/common-next';
import type { CardUpVariantType } from '../../types';
import { getPlpModuleSectionHeading, getTrimmedDisplayTitle } from '../../utils/plpModuleSectionHeading';
import { ColoredTextLink } from '../ColoredTextLink';
import CardUpVariant from './CardUpVariant';
import type { Card, CardUpBrandProps, CardUpProps } from './types';
import './CardInfo.scss';
import './CardUp.scss';
import './CardUpArticle.scss';
const CardUpArticleVariant = dynamic(() => import('./CardUpArticleVariant'), {
  ssr: false
});
const CardUpBrandVariant = dynamic(() => import('./CardUpBrandVariant'), {
  ssr: false
});
const ComponentCardUp: Record<CardUpVariantType, FC<CardUpProps>> = {
  '2up': props => <CardUpVariant sliceMax={2} {...props} />,
  '2up Brand': props => <CardUpBrandVariant sliceMax={2} {...props as unknown as CardUpBrandProps} />,
  '3up': props => <CardUpVariant sliceMax={3} {...props} />,
  '4up': props => <CardUpVariant sliceMax={4} {...props} />,
  '4Stack': props => <CardUpVariant sliceMax={4} {...props} />,
  '3up Article': props => <CardUpArticleVariant articlesPerView={3} {...props} />,
  Info: props => <CardUpVariant sliceMax={4} {...props} />
};
const validateCardTypes = (cards: Card[]): boolean => {
  const articleModel = cards.some(card => card.previewDescription);
  const articleModelsAll = cards.every(card => card.previewDescription);
  return articleModel && !articleModelsAll;
};
export default function CardUp(t0) {
  const $ = _c(48);
  const {
    header,
    description,
    linkAction,
    linkActionColor,
    variant,
    cards,
    backgroundColor,
    isH1
  } = t0;
  const pathname = usePathname();
  if (cards?.length === 0) {
    return null;
  }
  const Component = ComponentCardUp[variant];
  if (!Component) {
    return null;
  }
  if (variant === "3up Article" && validateCardTypes(cards)) {
    return null;
  }
  let t1;
  if ($[0] !== linkAction) {
    t1 = linkAction || {};
    $[0] = linkAction;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    link,
    actionText,
    internalTitle
  } = t1;
  let t2;
  if ($[2] !== header) {
    t2 = getTrimmedDisplayTitle(header);
    $[2] = header;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const displayHeader = t2;
  let t3;
  if ($[4] !== header || $[5] !== isH1) {
    t3 = getPlpModuleSectionHeading(header, isH1);
    $[4] = header;
    $[5] = isH1;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const {
    title: sectionTitle,
    headingTagName
  } = t3;
  let t4;
  if ($[7] !== actionText || $[8] !== displayHeader || $[9] !== header || $[10] !== internalTitle || $[11] !== link || $[12] !== linkActionColor || $[13] !== variant) {
    t4 = displayHeader && link && actionText ? <ColoredTextLink data-testid="link-action" onClick={() => {
      trackCardUpClick({
        variant,
        title: header,
        cardName: actionText,
        cardPosition: -1,
        promoDesc: internalTitle ?? ""
      });
    }} href={link?.webUrl} color={linkActionColor}>{actionText}</ColoredTextLink> : null;
    $[7] = actionText;
    $[8] = displayHeader;
    $[9] = header;
    $[10] = internalTitle;
    $[11] = link;
    $[12] = linkActionColor;
    $[13] = variant;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const titleAfter = t4;
  let isRestyled;
  let t5;
  let variantName;
  if ($[15] !== displayHeader || $[16] !== variant) {
    variantName = variant.toLowerCase().replaceAll(" ", "-");
    const noHeaderClass = !displayHeader ? `${variantName}--no-header` : "";
    let t6;
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = ["2up", "3up", "3up Article", "4up", "Info", "4Stack"];
      $[20] = t6;
    } else {
      t6 = $[20];
    }
    const restyledVariants = t6;
    isRestyled = restyledVariants.includes(variant);
    t5 = clsx("card-up", variantName, noHeaderClass, isRestyled && "card-up--restyled");
    $[15] = displayHeader;
    $[16] = variant;
    $[17] = isRestyled;
    $[18] = t5;
    $[19] = variantName;
  } else {
    isRestyled = $[17];
    t5 = $[18];
    variantName = $[19];
  }
  const componentClassName = t5;
  const cardHeaderTagName = isH1 || !displayHeader ? "h2" : "h3";
  const t6 = `card-up-container card-up-container--${variantName}`;
  let t7;
  if ($[21] !== backgroundColor) {
    t7 = {
      backgroundColor
    };
    $[21] = backgroundColor;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== isRestyled) {
    t8 = isRestyled && {
      align: "center",
      titleAfterLocation: "below-description"
    };
    $[23] = isRestyled;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  const t9 = pathname ?? "";
  let t10;
  if ($[25] !== Component || $[26] !== cardHeaderTagName || $[27] !== cards || $[28] !== description || $[29] !== header || $[30] !== isH1 || $[31] !== linkAction || $[32] !== t9 || $[33] !== variant) {
    t10 = <Component header={header} description={description} linkAction={linkAction} cards={cards} variant={variant} pathname={t9} isH1={isH1} cardHeaderTagName={cardHeaderTagName} />;
    $[25] = Component;
    $[26] = cardHeaderTagName;
    $[27] = cards;
    $[28] = description;
    $[29] = header;
    $[30] = isH1;
    $[31] = linkAction;
    $[32] = t9;
    $[33] = variant;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  let t11;
  if ($[35] !== componentClassName || $[36] !== description || $[37] !== headingTagName || $[38] !== sectionTitle || $[39] !== t10 || $[40] !== t7 || $[41] !== t8 || $[42] !== titleAfter || $[43] !== variantName) {
    t11 = <Section data-testid="card-up" data-variant={variantName} className={componentClassName} title={sectionTitle} description={description} titleAfter={titleAfter} style={t7} headingTagName={headingTagName} {...t8}>{t10}</Section>;
    $[35] = componentClassName;
    $[36] = description;
    $[37] = headingTagName;
    $[38] = sectionTitle;
    $[39] = t10;
    $[40] = t7;
    $[41] = t8;
    $[42] = titleAfter;
    $[43] = variantName;
    $[44] = t11;
  } else {
    t11 = $[44];
  }
  let t12;
  if ($[45] !== t11 || $[46] !== t6) {
    t12 = <LayoutContainer className={t6}>{t11}</LayoutContainer>;
    $[45] = t11;
    $[46] = t6;
    $[47] = t12;
  } else {
    t12 = $[47];
  }
  return t12;
}